// usePdfExport.js
// No extra dependencies — uses browser native print via hidden iframe
import logoSvg from "../../assets/logo.svg";

const allUserData = JSON.parse(localStorage.getItem('user_alldata'));
const logoDataUrl = await fetch(logoSvg)
  .then(r => r.blob())
  .then(blob => new Promise(res => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.readAsDataURL(blob);
  }));

export const VIDEO_EXTENSIONS = [
  "mp4","mpeg","webm","ogg","mov","avi","mkv",
  "flv","wmv","m4v","3gp","wav","aac","mp3","flac","x-m4a",
];

export const isVideo = (ext = "") =>
  VIDEO_EXTENSIONS.includes((ext || "").toLowerCase().replace(".", ""));

async function downloadFileToLocal(fileUrl, fileName) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", fileUrl, true);
    xhr.responseType = "blob";

    xhr.onload = function () {
      if (xhr.status === 200) {
        const forcedBlob = new Blob([xhr.response], { type: "application/octet-stream" });
        const objUrl     = URL.createObjectURL(forcedBlob);
        const a          = document.createElement("a");
        a.href          = objUrl;
        a.download      = fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(objUrl);
          resolve(true);
        }, 500);
      } else {
        console.warn(`[exportToPDF] XHR failed (${xhr.status}) for "${fileName}", opening in tab`);
        window.open(fileUrl, "_blank", "noopener,noreferrer");
        resolve(false);
      }
    };

    xhr.onerror = function () {
      console.warn(`[exportToPDF] XHR error for "${fileName}", opening in tab`);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      resolve(false);
    };

    xhr.send();
  });
}

function buildFileName(doc, index) {
  const raw = doc.document_name || doc.name || `attachment-${index + 1}`;
  const ext = doc.extension ? `.${doc.extension}` : "";
  return raw.includes(".") ? raw : raw + ext;
}

function extractYouTubeId(url = "") {
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function buildYouTubeLink(videoId) {
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
  const thumbUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  return `
    <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;margin:10px 0;
                background:#fff0f7;border:1px solid #f8b4d9;border-radius:8px;">
      <img src="${thumbUrl}" alt="YouTube thumbnail"
           style="width:120px;height:68px;object-fit:cover;border-radius:4px;flex-shrink:0;"
           onerror="this.style.display='none'"/>
      <div style="display:flex;flex-direction:column;gap:4px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span style="background:#FF0000;color:#fff;font-size:9px;font-weight:700;
                       padding:2px 7px;border-radius:3px;flex-shrink:0;">YouTube</span>
          <span style="font-size:11px;color:#9d1a6e;font-weight:600;">Video (not printable)</span>
        </div>
        <a href="${watchUrl}" target="_blank"
           style="color:#1A5298;font-size:12px;text-decoration:underline;word-break:break-all;">
          &#9654;&nbsp;${watchUrl}
        </a>
      </div>
    </div>`;
}

function replaceVideosWithLinks(html) {
  let result = (html || "")
    .replace(/<video[^>]+src=["']([^"']+)["'][^>]*>[\s\S]*?<\/video>/gi,
      (_, src) => `<a href="${src}" target="_blank" style="display:block;color:#1A5298;padding:8px 12px;margin:8px 0;background:#f5f0ff;border:1px solid #ddd6fe;border-radius:6px;text-decoration:underline;word-break:break-all;">&#9654;&nbsp;${src}</a>`)
    .replace(/<video[^>]*>([\s\S]*?)<\/video>/gi, (_, inner) => {
      const m = inner.match(/src=["']([^"']+)["']/i);
      const src = m ? m[1] : "";
      return src
        ? `<a href="${src}" target="_blank" style="display:block;color:#1A5298;padding:8px 12px;margin:8px 0;background:#f5f0ff;border:1px solid #ddd6fe;border-radius:6px;text-decoration:underline;word-break:break-all;">&#9654;&nbsp;${src}</a>`
        : "";
    });

  result = result.replace(/<iframe[^>]*>([\s\S]*?)<\/iframe>/gi, (fullMatch) => {
    const srcMatch = fullMatch.match(/\bsrc=["']([^"']+)["']/i);
    const src = srcMatch ? srcMatch[1] : "";
    if (!src) return "";
    const youtubeId = extractYouTubeId(src);
    if (youtubeId) return buildYouTubeLink(youtubeId);
    return `
      <div style="padding:8px 12px;margin:8px 0;background:#fdf4fb;border:1px solid #f0abdc;
                  border-radius:6px;font-size:12px;">
        <span style="font-weight:600;color:#9d1a6e;">Embedded content (not printable):</span><br/>
        <a href="${src}" target="_blank"
           style="color:#1A5298;text-decoration:underline;word-break:break-all;">&#9654;&nbsp;${src}</a>
      </div>`;
  });

  return result;
}

function showSaveAsPdfModal() {
  return new Promise((resolve) => {

    if (!document.getElementById("bt-modal-styles")) {
      const style = document.createElement("style");
      style.id = "bt-modal-styles";
      style.textContent = `
        @keyframes bt-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bt-modal-in {
          from { opacity: 0; transform: scale(0.88) translateY(20px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
        #bt-ok-btn:hover {
          background: #a8235f !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 8px 24px rgba(196,43,125,0.45) !important;
        }
        #bt-ok-btn:active  { transform: translateY(0) !important; }
        #bt-cancel-btn:hover {
          background: #f3f4f6 !important;
          color: #1a1a2e !important;
        }
      `;
      document.head.appendChild(style);
    }

    const backdrop = document.createElement("div");
    backdrop.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 999998;
      background: rgba(10, 8, 20, 0.60);
      backdrop-filter: blur(5px);
      -webkit-backdrop-filter: blur(5px);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: bt-backdrop-in 0.2s ease forwards;
    `;

    const modal = document.createElement("div");
    modal.style.cssText = `
      background: #ffffff;
      border-radius: 18px;
      width: 430px;
      max-width: 93vw;
      box-shadow: 0 28px 70px rgba(0,0,0,0.28), 0 0 0 1px rgba(196,43,125,0.12);
      font-family: Arial, Helvetica, sans-serif;
      overflow: hidden;
      animation: bt-modal-in 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards;
      position: relative;
      z-index: 999999;
    `;

    modal.innerHTML = `
      <div style="
        background: linear-gradient(135deg, #C42B7D 0%, #1A5298 100%);
        padding: 22px 24px 20px;
        display: flex;
        align-items: center;
        gap: 14px;
      ">
        <div>
          <div style="color:#fff; font-size:17px; font-weight:700; line-height:1.2;">
            Save as PDF
          </div>
          <div style="color:rgba(255,255,255,0.72); font-size:12px; margin-top:4px;">
            Follow these steps in the print dialog
          </div>
        </div>
      </div>

      <div style="padding: 22px 24px 8px;">
        <div style="display:flex; flex-direction:column; gap:14px; margin-bottom:20px;">

          <div style="display:flex; align-items:flex-start; gap:12px;">
            <div style="
              width:28px; height:28px; flex-shrink:0; border-radius:50%;
              background:#C42B7D; color:#fff; font-size:13px; font-weight:700;
              display:flex; align-items:center; justify-content:center; margin-top:1px;
            ">1</div>
            <div style="font-size:13px; color:#374151; line-height:1.55; padding-top:3px;">
              Click <b style="color:#1a1a2e;">OK</b> below — the browser
              <b style="color:#1a1a2e;">Print dialog</b> will open.
            </div>
          </div>

          <div style="display:flex; align-items:flex-start; gap:12px;">
            <div style="
              width:28px; height:28px; flex-shrink:0; border-radius:50%;
              background:#C42B7D; color:#fff; font-size:13px; font-weight:700;
              display:flex; align-items:center; justify-content:center; margin-top:1px;
            ">2</div>
            <div style="font-size:13px; color:#374151; line-height:1.55; padding-top:3px;">
              Find the <b style="color:#1a1a2e;">Destination</b> (or Printer) dropdown.
            </div>
          </div>

          <div style="display:flex; align-items:flex-start; gap:12px;">
            <div style="
              width:28px; height:28px; flex-shrink:0; border-radius:50%;
              background:#C42B7D; color:#fff; font-size:13px; font-weight:700;
              display:flex; align-items:center; justify-content:center; margin-top:1px;
            ">3</div>
            <div style="font-size:13px; color:#374151; line-height:1.55; padding-top:3px;">
              Select&nbsp;
              <span style="
                display:inline-flex; align-items:center; gap:5px;
                background:#fff0f7; border:1.5px solid #f8b4d9;
                color:#C42B7D; font-weight:700; font-size:12px;
                padding:2px 10px; border-radius:6px;
              ">Save as PDF</span>
              &nbsp;then click <b>Save</b>.
            </div>
          </div>

        </div>

        <div style="
          background:#f9fafb; border:1px solid #e5e7eb;
          border-radius:10px; padding:11px 14px; margin-bottom:22px;
          font-size:12px; color:#6b7280; line-height:1.7;
          display:flex; gap:10px; align-items:flex-start;
        ">
          <div>
            <b style="color:#1a1a2e;">Chrome / Edge:</b> Destination → <b>Save as PDF</b><br/>
            <b style="color:#1a1a2e;">Firefox:</b> Print to file → select <b>PDF</b><br/>
            <b style="color:#1a1a2e;">Safari:</b> Bottom-left PDF menu → <b>Save as PDF</b>
          </div>
        </div>
      </div>

      <div style="
        display:flex; gap:10px;
        padding: 0 24px 22px;
      ">
        <button id="bt-cancel-btn" style="
          flex: 1; padding: 11px 0; border-radius: 9px;
          border: 1.5px solid #e5e7eb; background: #fff;
          color: #6b7280; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.15s ease;
          font-family: Arial, Helvetica, sans-serif;
        ">Cancel</button>

        <button id="bt-ok-btn" style="
          flex: 2; padding: 12px 0; border-radius: 9px;
          border: none; background: #C42B7D;
          color: #fff; font-size: 13px; font-weight: 700;
          cursor: pointer; transition: all 0.15s ease;
          box-shadow: 0 4px 16px rgba(196,43,125,0.32);
          display: flex; align-items: center; justify-content: center; gap: 8px;
          font-family: Arial, Helvetica, sans-serif;
        ">
         OK, Open Print Dialog
        </button>
      </div>
    `;

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    setTimeout(() => modal.querySelector("#bt-ok-btn")?.focus(), 120);

    const cleanup = () => {
      if (document.body.contains(backdrop)) document.body.removeChild(backdrop);
      document.removeEventListener("keydown", onKey);
    };

    const onKey = (e) => {
      if (e.key === "Escape") { cleanup(); resolve(false); }
      if (e.key === "Enter")  { cleanup(); resolve(true);  }
    };
    document.addEventListener("keydown", onKey);

    modal.querySelector("#bt-ok-btn").addEventListener("click", () => {
      cleanup(); resolve(true);
    });
    modal.querySelector("#bt-cancel-btn").addEventListener("click", () => {
      cleanup(); resolve(false);
    });

    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) { cleanup(); resolve(false); }
    });
  });
}

export async function exportToPDF({
  article         = null,
  articleContent  = "",
  withAttachments = false,
  documents       = [],
  selectedDocIds  = [],
  onProgress      = () => {},
  fileName        = "KBA-Article",
}) {
  onProgress(10);

  const authorName = article?.author?.name   || "";
  const group      = article?.owned_by_group || "";
  const exportName = allUserData             || "";
  const formatId   = article?.format_id      || "";
  const title      = article?.title          || "";

  const nonVideoDocs = Array.isArray(documents)
    ? documents.filter(d => !isVideo(d.extension)) : [];

  const bodyHTML = replaceVideosWithLinks(articleContent);

  const attachmentHTML = nonVideoDocs.length > 0 ? `
    <div style="border:1px solid #f8b4d9;border-radius:6px;padding:12px 16px;margin-bottom:20px;background:#fff0f7;">
      <div style="font-size:10px;font-weight:700;color:#C42B7D;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">Attachments</div>
      ${nonVideoDocs.map(doc => `
        <div style="display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid #fce7f3;font-size:12px;">
          <span style="background:#C42B7D;color:#fff;font-size:9px;font-weight:700;padding:2px 6px;border-radius:3px;flex-shrink:0;">
            ${(doc.extension || "FILE").toUpperCase()}
          </span>
          <span style="color:#1a1a2e;">${doc.document_name || doc.name || ""}</span>
        </div>
      `).join("")}
    </div>
  ` : "";

  const printHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>${fileName}</title>
  <style>
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 1.75;
      background: #fff8f5;
      color: #1a1a2e;
      margin: 0;
      padding: 36px 48px;
    }
    .bt-header-bar {
      background: #fff;
      padding: 14px 20px;
      border-radius: 8px;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .bt-header-bar .bt-section-label {
      font-size: 11px;
      opacity: 0.75;
      margin-left: auto;
      letter-spacing: 0.04em;
    }
    .kba-format-id {
      font-size: 11px;
      font-weight: 700;
      color: #C42B7D;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 8px;
    }
    .kba-title {
      font-size: 22px;
      font-weight: 700;
      color: #1a1a2e;
      line-height: 1.3;
      margin-bottom: 12px;
    }
    .kba-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      font-size: 12px;
      color: #6b7280;
      padding: 10px 14px 14px;
      border-bottom: 2px solid #f8b4d9;
      background: #fff0f7;
      border-radius: 6px;
      margin-bottom: 18px;
    }
    .kba-meta b { color: #C42B7D; }
    .article-body {
      font-size: 13px;
      line-height: 1.8;
      background: #ffffff;
      padding: 18px 20px;
      border-radius: 8px;
      border: 1px solid #fce7f3;
    }
    .article-body img   { max-width: 100%; height: auto; }
    .article-body table { width: 100%; border-collapse: collapse; }
    .article-body th    { background: #C42B7D; color: #ffffff; border: 1px solid #f8b4d9; padding: 6px 10px; }
    .article-body td    { border: 1px solid #fce7f3; padding: 6px 10px; }
    .article-body ul,
    .article-body ol    { padding-left: 22px; }
    .article-body pre   { overflow-x: auto; white-space: pre-wrap; word-break: break-word; background: #fdf4fb; border: 1px solid #f8b4d9; border-radius: 4px; padding: 10px; }
    .article-body a     { color: #1A5298; }
    .bt-footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1.5px solid #f8b4d9;
      font-size: 10px;
      color: #C42B7D;
      text-align: center;
      letter-spacing: 0.05em;
    }
    @page { size: A4; margin: 18mm 16mm; }
    @media print { body { padding: 0; background: #fff8f5; } }
  </style>
</head>
<body>
  <div class="bt-header-bar">
    <span>
      <img src="${logoDataUrl}" alt="BenchTrack" style="height:22px;vertical-align:middle;" />
    </span>
    <span class="bt-section-label">Knowledge Article</span>
  </div>

  ${formatId ? `<div class="kba-format-id">${formatId}</div>` : ""}
  <div class="kba-title">${title}</div>

  <div class="kba-meta">
    ${authorName ? `<div><b>Authored by:</b> ${authorName}</div>` : ""}
    ${group      ? `<div><b>Group:</b> ${group}</div>`            : ""}
    ${exportName ? `<div><b>Exported by:</b> ${exportName?.first_name + " " + exportName?.last_name}</div>` : ""}
  </div>

  ${attachmentHTML}

  <div class="article-body">${bodyHTML}</div>

  <div class="bt-footer">BenchTrack &mdash; Knowledge Article &mdash; Exported Document</div>
</body>
</html>`;

  onProgress(40);

  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;";
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  iframeDoc.open();
  iframeDoc.write(printHTML);
  iframeDoc.close();

  onProgress(60);

  await new Promise(resolve => {
    if (iframe.contentDocument.readyState === "complete") {
      setTimeout(resolve, 400);
    } else {
      iframe.contentWindow.onload = () => setTimeout(resolve, 300);
      setTimeout(resolve, 1500);
    }
  });

  onProgress(75);

  const confirmed = await showSaveAsPdfModal();

  if (!confirmed) {
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
    onProgress(0);
    return false; // ✅ user cancelled second modal
  }

  onProgress(85);

  iframe.contentWindow.focus();
  iframe.contentWindow.print();

  setTimeout(() => {
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
  }, 5000);

  onProgress(90);

  // ── Download attachments ──────────────────────────────────────────────────
  if (withAttachments) {
    const included = (Array.isArray(documents) ? documents : []).filter(d => {
      if (isVideo(d.extension)) return false;
      if (selectedDocIds.length === 0) return false; // ✅ nothing selected = nothing downloads
      return selectedDocIds.includes(String(d.id));  // ✅ only download selected
    });

    console.log(`[exportToPDF] Downloading ${included.length} attachment(s)...`);

    for (const [i, doc] of included.entries()) {
      if (!doc.download_url) {
        console.warn(`[exportToPDF] Skipping "${doc.document_name || doc.name}" — no download_url`);
        continue;
      }
      console.log(`[exportToPDF] Downloading (${i + 1}/${included.length}): ${doc.download_url}`);
      await downloadFileToLocal(doc.download_url, buildFileName(doc, i));
      onProgress(90 + Math.round(((i + 1) / included.length) * 9));
    }
  }

  onProgress(100);
  return true; // ✅ success
}